import { ReactNode, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from './Navbar';
import Footer from '../../pages/home/components/Footer';
import { useSEO } from '../../hooks/useSEO';

interface Section {
  id: string;
  title: string;
}

interface LegalSEOProps {
  title: string;
  description: string;
  canonical: string;
  keywords?: string;
  schema?: object;
}

interface LegalPageLayoutProps {
  title: string;
  lastUpdated: string;
  sections: Section[];
  children: ReactNode;
  seo?: LegalSEOProps;
}

function LegalSEO({ seo }: { seo: LegalSEOProps }) {
  useSEO({
    title: seo.title,
    description: seo.description,
    keywords: seo.keywords,
    canonical: seo.canonical,
    ogTitle: seo.title,
    ogDescription: seo.description,
    ogType: 'website',
    ogUrl: seo.canonical,
    schema: seo.schema,
  });
  return null;
}

export default function LegalPageLayout({ title, lastUpdated, sections, children, seo }: LegalPageLayoutProps) {
  const [activeSection, setActiveSection] = useState<string>('');

  useEffect(() => {
    window.scrollTo(0, 0);

    const handleScroll = () => {
      const sectionElements = sections.map(section => ({
        id: section.id,
        element: document.getElementById(section.id)
      }));

      const currentSection = sectionElements.find(({ element }) => {
        if (!element) return false;
        const rect = element.getBoundingClientRect();
        return rect.top <= 150 && rect.bottom >= 150;
      });

      if (currentSection) {
        setActiveSection(currentSection.id);
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, [sections]);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 100;
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({
        top: elementPosition - offset,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="min-h-screen bg-gray-950">
      {seo && <LegalSEO seo={seo} />}
      <Navbar />
      
      <div className="pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-teal-400 hover:text-teal-300 transition-colors mb-8 group"
          >
            <i className="ri-arrow-left-line text-xl group-hover:-translate-x-1 transition-transform"></i>
            <span>Back to Home</span>
          </Link>

          <div className="mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">{title}</h1>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-teal-500/10 border border-teal-500/20 rounded-full">
              <i className="ri-calendar-line text-teal-400"></i>
              <span className="text-sm text-teal-300">Last Updated: {lastUpdated}</span>
            </div>
          </div>

          <div className="grid lg:grid-cols-4 gap-12">
            {/* Table of Contents Sidebar */}
            <div className="lg:col-span-1">
              <div className="lg:sticky lg:top-32">
                <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-6">
                  <h3 className="text-sm font-bold text-gray-400 uppercase tracking-widest mb-4">
                    Table of Contents
                  </h3>
                  <nav className="space-y-2">
                    {sections.map((section) => (
                      <button
                        key={section.id}
                        onClick={() => scrollToSection(section.id)}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all duration-200 cursor-pointer ${
                          activeSection === section.id
                            ? 'bg-teal-500/20 text-teal-400 border-l-2 border-teal-500'
                            : 'text-gray-400 hover:text-teal-400 hover:bg-gray-700/50'
                        }`}
                      >
                        {section.title}
                      </button>
                    ))}
                  </nav>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="lg:col-span-3">
              <div className="max-w-[720px] mx-auto">
                <div className="bg-gray-900 border border-gray-700 rounded-2xl p-8 md:p-14 shadow-xl">
                  <div className="prose prose-invert prose-lg max-w-none
                    [&_*]:text-white [&_h2]:text-teal-400 [&_h3]:text-teal-300
                    prose-headings:font-bold
                    prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-6 prose-h2:border-b prose-h2:border-teal-500/30 prose-h2:pb-4 prose-h2:scroll-mt-32
                    prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-4
                    prose-p:text-white prose-p:leading-[1.95] prose-p:text-[1.0625rem] prose-p:mb-6
                    prose-strong:text-white prose-strong:font-semibold
                    prose-a:text-teal-400 prose-a:no-underline hover:prose-a:text-teal-300
                    prose-ul:text-white prose-ol:text-white
                    prose-li:mb-2 prose-li:text-white">
                    {children}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}